import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isHttpsUrl, httpsUrlErrorMessage } from '../../lib/url-validators';
import { UpdateGameCatalogInput } from '../games/games-types';

/**
 * v0.3.0 Game Catalog form. Uses react-hook-form + zod for client-side
 * validation that mirrors the backend DTO rules.
 *
 * RT-R11: every URL field goes through `isHttpsUrl` (CIDR + protocol + TLD
 * checks). The frontend shows immediate feedback; backend remains the
 * authoritative validator.
 *
 * RT-R2: status is the closed enum (draft | published | archived).
 */
const urlField = z
  .string()
  .url()
  .refine(isHttpsUrl, { message: httpsUrlErrorMessage() })
  .optional()
  .or(z.literal(''));

const formSchema = z.object({
  description: z.string().max(10_000).optional().or(z.literal('')),
  icon_url: urlField,
  ios_url: urlField,
  android_url: urlField,
  web_url: urlField,
  harmonyos_url: urlField,
  website_url: urlField,
  status: z.enum(['draft', 'published', 'archived']).optional(),
});

export type GameCatalogFormValues = z.infer<typeof formSchema>;

export interface GameCatalogFormProps {
  defaultValues: UpdateGameCatalogInput;
  onSubmit: (values: UpdateGameCatalogInput) => void;
  isSubmitting?: boolean;
}

export function GameCatalogForm({
  defaultValues,
  onSubmit,
  isSubmitting = false,
}: GameCatalogFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<GameCatalogFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: defaultValues.description ?? '',
      icon_url: defaultValues.icon_url ?? '',
      ios_url: defaultValues.ios_url ?? '',
      android_url: defaultValues.android_url ?? '',
      web_url: defaultValues.web_url ?? '',
      harmonyos_url: defaultValues.harmonyos_url ?? '',
      website_url: defaultValues.website_url ?? '',
      status: defaultValues.status,
    },
  });

  // Reset form when defaultValues change (e.g., catalog fetched after load)
  useEffect(() => {
    reset({
      description: defaultValues.description ?? '',
      icon_url: defaultValues.icon_url ?? '',
      ios_url: defaultValues.ios_url ?? '',
      android_url: defaultValues.android_url ?? '',
      web_url: defaultValues.web_url ?? '',
      harmonyos_url: defaultValues.harmonyos_url ?? '',
      website_url: defaultValues.website_url ?? '',
      status: defaultValues.status,
    });
  }, [defaultValues, reset]);

  const handleFormSubmit = (values: GameCatalogFormValues) => {
    // Strip empty strings so backend gets undefined → no-op per PATCH semantics
    const payload: UpdateGameCatalogInput = {};
    if (values.description) payload.description = values.description;
    if (values.icon_url) payload.icon_url = values.icon_url;
    if (values.ios_url) payload.ios_url = values.ios_url;
    if (values.android_url) payload.android_url = values.android_url;
    if (values.web_url) payload.web_url = values.web_url;
    if (values.harmonyos_url) payload.harmonyos_url = values.harmonyos_url;
    if (values.website_url) payload.website_url = values.website_url;
    if (values.status) payload.status = values.status;
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Field label="Description" error={errors.description?.message}>
        <textarea
          {...register('description')}
          rows={4}
          className="w-full border rounded px-2 py-1"
          placeholder="Short description (max 10000 chars)"
        />
      </Field>

      <Field label="Icon URL" error={errors.icon_url?.message}>
        <input
          {...register('icon_url')}
          className="w-full border rounded px-2 py-1"
          placeholder="https://cdn.example.com/icon.png"
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="iOS App Store" error={errors.ios_url?.message}>
          <input {...register('ios_url')} className="w-full border rounded px-2 py-1" placeholder="https://apps.apple.com/..." />
        </Field>
        <Field label="Google Play" error={errors.android_url?.message}>
          <input {...register('android_url')} className="w-full border rounded px-2 py-1" placeholder="https://play.google.com/..." />
        </Field>
        <Field label="Web" error={errors.web_url?.message}>
          <input {...register('web_url')} className="w-full border rounded px-2 py-1" placeholder="https://game.example.com" />
        </Field>
        <Field label="HarmonyOS AppGallery" error={errors.harmonyos_url?.message}>
          <input {...register('harmonyos_url')} className="w-full border rounded px-2 py-1" placeholder="https://appgallery.huawei.com/..." />
        </Field>
      </div>

      <Field label="Website" error={errors.website_url?.message}>
        <input
          {...register('website_url')}
          className="w-full border rounded px-2 py-1"
          placeholder="https://example.com/about"
        />
      </Field>

      <Field label="Status" error={errors.status?.message}>
        <select {...register('status')} className="w-full border rounded px-2 py-1">
          <option value="">— select —</option>
          <option value="draft">Draft (hidden from public more-games)</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </Field>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!isDirty || isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {isSubmitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1">{label}</span>
      {children}
      {error && <span className="block text-xs text-red-600 mt-1">{error}</span>}
    </label>
  );
}